import React, { useCallback, useMemo, useState } from 'react';
import TableCommonHook from '@op/components/TableCommonHook';
import { percentageFormat, reviewsFormat, sentimentFormat } from '@op/utils/value';
import { useIntl } from 'umi';
import { dealWithOthersName } from '@op/utils/languageValue';
import { channelInfo } from '@op/components/ChannelLabel';
import socialmedia from '@/pages/opinion/assets/svg/channel/socialmedia.svg';
import useFullScreen from '@op/hooks/useFullScreen';
import { NO_DATE } from '@op/constants';
import './index.scss';
import PercentageItem from '@op/components/Snippets/PercentageItem';
import RingRatioBubble from '@op/components/Snippets/RingRatioBubble';
import { formatDateFunc, getFilterDateQOQText } from '@op/utils/time';
import moment from 'moment';
import { isOneDay } from '@op/utils';

const ChannelLanguageTable = (props: {
  filter: any;
  header: string;
  records: Array<any>;
  eventsDict?: { [key: string]: any };
  tableWrapper?: (element: any, tips?: any, props?: any) => React.ReactNode;
}) => {
  const {
    filter,
    header,
    records = [],
    eventsDict = {},
    tableWrapper = (element) => element,
  } = props;
  const { formatMessage } = useIntl();
  const { ScreenModal, FullIcon } = useFullScreen();

  const timeShow = useCallback(
    (timeRange: [string, string]) => {
      const [start_time, end_time] = timeRange;

      if (filter.date_type === 'hourly') {
        return [
          `${moment(start_time).format('YYYY-MM-DD HH:mm')}~${moment(
            end_time,
          ).format('YYYY-MM-DD HH:mm')}`,
        ];
      }
      if (isOneDay(filter)) {
        return [moment(start_time).format('YYYY-MM-DD')];
      }
      return [
        `${moment(start_time).format('YYYY-MM-DD')}~${moment(end_time).format(
          'YYYY-MM-DD',
        )}`,
      ];
    },
    [filter],
  );

  const { text } = getFilterDateQOQText(filter);
  const tableColumns = useMemo(() => {
    const columns: any = [
      {
        key: 'name',
        header: (
          <FullIcon
            title={formatMessage({ id: header || NO_DATE.EMPTY })}
          ></FullIcon>
        ),
        align: 'left',
        render: (cvm: any) => {
          if (header === 'Channel') {
            const { img } = channelInfo(cvm.name, socialmedia);
            return tableWrapper(
              <div className={`channel-language-div`}>
                <img src={img} />
                <span className="channel-language-span">
                  {dealWithOthersName(cvm.name, header.toLocaleLowerCase())}
                </span>
              </div>,
            );
          }
          return tableWrapper(dealWithOthersName(cvm.name, header.toLocaleLowerCase()));
        },
        width: 220,
        fixed: 'left'
      },
      {
        key: 'count',
        header: 'Total Mentions',
        render: (cvm: any) => reviewsFormat(cvm.count),
        align: 'right',
        width: 180,
      },
      {
        key: 'count_change',
        header: 'Total Mentions Change%',
        render: (cvm: any) => (
          <RingRatioBubble
            newTime={timeShow(cvm?.newTime)}
            oldTime={timeShow(cvm?.oldTime)}
            oldValue={cvm?.old_count}
            newValue={cvm?.count}
            formateValue={reviewsFormat}
            title={`${formatMessage({ id: 'Total Mentions' })} ${formatMessage({
              id: text,
            })}`}
          >
            <PercentageItem percentage={cvm?.count_change * 100}></PercentageItem>
          </RingRatioBubble>

        ),
        align: 'right',
        width: 270,
      },
      {
        key: 'avg_sentiment',
        header: 'Sentiment',
        render: (cvm: any) => sentimentFormat(cvm.avg_sentiment),
        align: 'right',
        width: 160,
      },
      {
        key: 'positive',
        header: 'Positive%',
        render: (cvm: any) => percentageFormat(cvm.positive * 100),
        align: 'right',
        width: 145,
      },
      {
        key: 'neutral',
        header: 'Neutral%',
        render: (cvm: any) => percentageFormat(cvm.neutral * 100),
        align: 'right',
        width: 145,
      },
      {
        key: 'negative',
        header: 'Negative%',
        render: (cvm: any) => percentageFormat(cvm.negative * 100),
        align: 'right',
        width: 155,
      },
    ];

    return columns.map((item: any) => {
      const { header } = item;
      return {
        ...item,
        header:
          typeof header === 'string' ? formatMessage({ id: header }) : header,
      };
    });
  }, [header, formatMessage]);

  function TableCommon(props: any) {
    return (
      <TableCommonHook
        recordKey={'name'}
        className="dbo-channel-language-table-wrapper"
        columns={tableColumns}
        total={records?.length}
        records={records}
        hasPageable={false}
        maxHeight={293}
        minWidth={1400}
        sortColumns={tableColumns?.filter((val) => val.key !== 'name').map((val) => val.key)}
        sortType={'local'}
        defaultSort={{
          by: 'count',
          order: 'desc'
        }}
        {...props}
        {...eventsDict}
      />
    );
  }

  return (
    <>
      <TableCommon></TableCommon>

      <ScreenModal title={formatMessage({ id: header })}>
        <TableCommon maxHeight={500}></TableCommon>
      </ScreenModal>
    </>
  );
};

export default ChannelLanguageTable;
